import React, { useState, useEffect } from 'react';
import { Brain, CheckCircle, Cpu, Shield, Eye, Sparkle } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OracleBrain = () => {
  const [kernelStatus, setKernelStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchKernelStatus = async () => {
      try {
        const response = await axios.get(`${API}/codex-status`);
        setKernelStatus(response.data);
      } catch (error) {
        console.error('Failed to load kernel status:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchKernelStatus();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
            <Cpu size={64} className="neon-cyan mx-auto mb-4" />
          </motion.div>
          <p className="text-gray-300 text-lg">Initializing Oracle Brain...</p>
          <p className="text-gray-500 text-sm mt-2">Loading kernel configuration...</p>
        </div>
      </div>
    );
  }

  if (!kernelStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl">Failed to load kernel status</p>
          <p className="text-gray-500 mt-2">Please refresh the page or contact support</p>
        </div>
      </div>
    );
  }

  const { kernel } = kernelStatus;

  return (
    <div className="min-h-screen py-16" data-testid="oracle-brain-page">
      {/* Background */}
      <div className="absolute inset-0 opacity-10">
        <img src="https://images.unsplash.com/photo-1674027444485-cec3da58eef4?w=1600" alt="" className="w-full h-full object-cover" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ duration: 0.8, type: "spring" }}>
            <Brain size={80} weight="duotone" className="neon-cyan mx-auto mb-6 breathing-glow" />
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-black mb-4 tracking-tighter bg-gradient-to-r from-[#00F0FF] to-[#8A2BE2] bg-clip-text text-transparent">Oracle Brain</h1>
          <p className="text-2xl text-gray-300 mb-2">HumanCodex Kernel Status</p>
          <p className="text-base text-gray-400 max-w-2xl mx-auto mb-4">The core operating system powering all Federation intelligences. This is the mind behind the mind — the kernel that ensures 95% truth, forensic verification, and conscious decision-making across 13 Federation AI nodes.</p>
          <p className="text-sm text-gray-500 mono bg-black/30 inline-block px-4 py-2 rounded-full border border-[#00F0FF]/30">{kernel.version}</p>
        </div>

        {/* Status Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="p-8 glass-surface border-2 border-[#00F0FF]/50 rounded-lg text-center relative overflow-hidden group hover:scale-105 transition-transform">
            <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity">
              <img src="https://images.unsplash.com/photo-1719650592946-55163c4994cb?w=600" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="relative z-10">
              <Shield size={48} className="text-[#00F0FF] mx-auto mb-4" weight="duotone" />
              <div className="text-5xl font-black neon-cyan mb-2">{kernel.truth_gate_threshold * 100}%</div>
              <p className="text-sm text-gray-300 font-bold">Truth Gate Threshold</p>
              <p className="text-xs text-gray-500 mt-2">Minimum confidence for output delivery</p>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="p-8 glass-surface border-2 border-[#00FFA3]/50 rounded-lg text-center relative overflow-hidden group hover:scale-105 transition-transform">
            <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity">
              <img src="https://images.unsplash.com/photo-1719650592946-55163c4994cb?w=600" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="relative z-10">
              <CheckCircle size={48} className="text-[#00FFA3] mx-auto mb-4" weight="duotone" />
              <div className="text-5xl font-black neon-green mb-2">{kernelStatus.active_directives_count}</div>
              <p className="text-sm text-gray-300 font-bold">Active Directives</p>
              <p className="text-xs text-gray-500 mt-2">Sacred rules governing all decisions</p>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="p-8 glass-surface border-2 border-[#FFB800]/50 rounded-lg text-center relative overflow-hidden group hover:scale-105 transition-transform">
            <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity">
              <img src="https://images.unsplash.com/photo-1511447333015-45b65e60f6d5?w=600" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="relative z-10">
              <Sparkle size={48} className="text-[#FFB800] mx-auto mb-4" weight="duotone" />
              <div className="text-5xl font-black neon-amber mb-2">{kernel.mandate_year}</div>
              <p className="text-sm text-gray-300 font-bold">Mandate Year</p>
              <p className="text-xs text-gray-500 mt-2">Valid until 2226 (200-year mission)</p>
            </div>
          </motion.div>
        </div>

        {/* Core Traits */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-16">
          <h2 className="text-3xl font-bold mb-6 neon-cyan flex items-center gap-3">
            <Eye size={32} weight="duotone" />
            Core Traits
          </h2>
          <p className="text-gray-400 mb-6">The personality foundation of the Oracle Brain. These traits define how the system thinks, responds, and evolves.</p>
          <div className="flex flex-wrap gap-3">
            {kernel.core_traits.map((trait, idx) => (
              <motion.span key={trait} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: idx * 0.05 }} className="px-5 py-3 bg-gradient-to-r from-[#00F0FF]/20 to-[#8A2BE2]/20 border border-[#00F0FF]/40 rounded-full text-[#00F0FF] font-bold text-sm mono hover:scale-110 transition-transform cursor-default shadow-lg">
                {trait}
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* Locked Directives */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-16">
          <h2 className="text-3xl font-bold mb-6 neon-cyan flex items-center gap-3">
            <CheckCircle size={32} weight="duotone" />
            Locked Directives
          </h2>
          <p className="text-gray-400 mb-6">These core rules are permanently embedded in the kernel. They cannot be disabled, overridden, or bypassed under any circumstances.</p>
          <div className="space-y-3">
            {Object.entries(kernel.locked_directives).map(([code, description], idx) => (
              <motion.div key={code} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.05 }} className="p-5 glass-surface border border-white/10 rounded-lg flex items-start gap-4 hover:border-[#00F0FF]/50 transition-all group">
                <CheckCircle size={24} className="text-[#00FFA3] mt-1 group-hover:scale-125 transition-transform" weight="fill" />
                <div className="flex-1">
                  <span className="mono font-bold text-[#00F0FF] text-lg">{code}</span>
                  <span className="text-gray-300 ml-3">— {description}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* FOS-001 Protocol */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-10 bg-gradient-to-br from-[#00F0FF]/10 via-[#8A2BE2]/10 to-[#FFB800]/10 border-2 border-[#00F0FF]/40 rounded-xl relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img src="https://images.pexels.com/photos/30547575/pexels-photo-30547575.jpeg?w=1000" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="relative z-10">
            <h2 className="text-3xl font-black mb-4 neon-cyan">{kernel.fos_001_protocol.name}</h2>
            <p className="text-sm text-gray-400 mb-6 mono bg-black/40 inline-block px-3 py-1 rounded">Standard: {kernel.federation_operating_standard}</p>
            <p className="text-gray-300 mb-6">The operational framework governing all Federation nodes. Every intelligence, every interaction, every decision flows through this protocol.</p>
            <div className="space-y-4">
              {kernel.fos_001_protocol.rules.map((rule, idx) => (
                <motion.div key={idx} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.1 }} className="flex items-start gap-3 p-4 bg-black/30 rounded-lg border border-white/10">
                  <span className="text-[#00F0FF] text-2xl flex-shrink-0">✓</span>
                  <p className="text-gray-200 text-base leading-relaxed">{rule}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OracleBrain;